/**
 * Guest Identity Service
 * Centralise toute la logique d'identification des utilisateurs invités.
 *
 * Deux identifiants complémentaires :
 * - deviceId  : identifiant local stable (localStorage), utilisé comme creator_id
 * - fingerprint : empreinte navigateur (async), utilisé pour les requêtes Supabase (guest_fingerprint)
 *
 * Ce service est la source unique de vérité pour l'identité guest.
 * Les autres modules (pollStorage, dashboard, PollService) doivent l'utiliser
 * au lieu de recalculer ces valeurs indépendamment.
 */

import { logger } from "./logger";

// ============================================================================
// CONSTANTS
// ============================================================================

const DEVICE_ID_STORAGE_KEY = "doodates_device_id";

// ============================================================================
// CACHE (module-level pour stabilité dans les tests)
// ============================================================================

let deviceIdCache: string | null = null;
let fingerprintCache: string | null = null;

// ============================================================================
// DEVICE ID — synchrone, stable, localStorage
// ============================================================================

function hasWindow(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * Retourne le deviceId stable de cet appareil.
 * Génère et persiste un nouvel ID si aucun n'existe.
 */
export function getDeviceId(): string {
  if (deviceIdCache) return deviceIdCache;
  if (!hasWindow()) return (deviceIdCache = "server");

  const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing && existing.trim()) {
    // Valider que l'UUID existant est valide (format standard UUID v4)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(existing)) {
      deviceIdCache = existing;
      return existing;
    } else if (existing.startsWith("dev-")) {
      // Migration: retirer le préfixe dev- des anciens UUIDs
      const cleanUuid = existing.substring(4);
      if (uuidRegex.test(cleanUuid)) {
        window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, cleanUuid);
        deviceIdCache = cleanUuid;
        console.log("✅ Migrated dev- prefixed UUID to standard UUID");
        return cleanUuid;
      }
    }
    // UUID invalide, en générer un nouveau
    console.warn(
      "⚠️ UUID invalide détecté dans localStorage, génération d'un nouvel UUID",
    );
    window.localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  }

  // Générer un deviceId UUID valide (sans préfixe pour compatibilité PostgreSQL)
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    },
  );
  window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, uuid);
  deviceIdCache = uuid;
  return uuid;
}

// ============================================================================
// FINGERPRINT — async, basé sur le navigateur
// ============================================================================

/**
 * Retourne le fingerprint navigateur (async).
 * Utilise le cache mémoire si disponible, sinon délègue à browserFingerprint.
 */
export async function getFingerprint(): Promise<string> {
  if (fingerprintCache) return fingerprintCache;

  try {
    const { getCachedFingerprint } = await import("./browserFingerprint");
    fingerprintCache = await getCachedFingerprint();
    return fingerprintCache;
  } catch (error) {
    logger.warn(
      "⚠️ Impossible de récupérer le fingerprint, fallback deviceId",
      "auth",
    );
    return getDeviceId();
  }
}

// ============================================================================
// IDENTITÉ COMPLÈTE
// ============================================================================

export interface GuestIdentity {
  deviceId: string;
  fingerprint: string;
  stableFingerprint: string;
}

/**
 * Retourne le fingerprint "stable" (clé secondaire cross-browser).
 */
export async function getStableFingerprint(): Promise<string> {
  try {
    const { getCachedStableFingerprint } = await import("./browserFingerprint");
    return await getCachedStableFingerprint();
  } catch (error) {
    logger.warn("Stable fingerprint unavailable", "auth", { error });
    return "";
  }
}

/**
 * Retourne l'identité complète de l'invité (deviceId + fingerprint + stable).
 */
export async function getGuestIdentity(): Promise<GuestIdentity> {
  const deviceId = getDeviceId();
  const [fingerprint, stableFingerprint] = await Promise.all([
    getFingerprint(),
    getStableFingerprint(),
  ]);
  return { deviceId, fingerprint, stableFingerprint };
}

// ============================================================================
// JETON DE POSSESSION DES SONDAGES INVITÉS (#828)
// ============================================================================

const GUEST_OWNER_TOKEN_KEY = "__dd_guest_owner_token";

let guestOwnerTokenCache: string | null = null;

/**
 * Retourne le jeton de possession des sondages invités de ce navigateur
 * (généré à la demande, persisté sans expiration).
 *
 * Sécurité (#828) : la propriété des sondages invités reposait sur le
 * fingerprint navigateur — dérivable par n'importe quel site, donc
 * usurpable (IDOR sur delete/set_poll_status/claim). Ce jeton aléatoire
 * 256 bits est inforgeable ; le serveur n'en stocke que le hash SHA-256
 * (polls.guest_owner_token_hash). Ne JAMAIS l'exposer hors des appels RPC
 * de propriété. Retourne null hors navigateur (SSR) ou sans WebCrypto.
 */
export function getGuestOwnerToken(): string | null {
  if (guestOwnerTokenCache) return guestOwnerTokenCache;
  if (!hasWindow()) return null;

  try {
    const existing = window.localStorage.getItem(GUEST_OWNER_TOKEN_KEY);
    // Longueur minimale alignée sur la validation serveur (>= 32 chars).
    if (existing && existing.trim().length >= 32) {
      guestOwnerTokenCache = existing;
      return existing;
    }

    const cryptoObj = globalThis.crypto;
    if (!cryptoObj || typeof cryptoObj.getRandomValues !== "function") {
      return null;
    }
    const bytes = cryptoObj.getRandomValues(new Uint8Array(32));
    const token = Array.from(bytes, (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
    window.localStorage.setItem(GUEST_OWNER_TOKEN_KEY, token);
    guestOwnerTokenCache = token;
    return token;
  } catch (error) {
    logger.warn("Guest owner token unavailable", "auth", { error });
    return null;
  }
}

// ============================================================================
// CURRENT USER ID — unifié auth + guest
// ============================================================================

/**
 * Retourne l'identifiant de l'utilisateur courant.
 * - Si authentifié : retourne l'ID Supabase
 * - Si invité : retourne le deviceId
 */
export function getCurrentUserId(authenticatedUserId?: string | null): string {
  if (authenticatedUserId) {
    return authenticatedUserId;
  }
  return getDeviceId();
}

// ============================================================================
// HELPERS POUR LES POLLS
// ============================================================================

/**
 * Enrichit les données d'un poll avec les identifiants guest.
 * Pour les invités : creator_id = null (colonne UUID, pas de deviceId),
 * identification via guest_fingerprint uniquement.
 * Pour les connectés : creator_id inchangé (UUID Supabase).
 */
export async function enrichPollForGuest(poll: {
  creator_id?: string | null;
  guest_fingerprint?: string | null;
}): Promise<{ creator_id: string | null; guest_fingerprint: string }> {
  const { fingerprint } = await getGuestIdentity();

  // Si creator_id est un UUID valide (utilisateur connecté), le garder
  // Sinon (guest, anonymous, deviceId, undefined) → null
  const isValidUuid =
    poll.creator_id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      poll.creator_id,
    );
  const creatorId = isValidUuid ? poll.creator_id! : null;

  return {
    creator_id: creatorId,
    guest_fingerprint: fingerprint,
  };
}

// ============================================================================
// HELPERS POUR LES CONVERSATIONS
// ============================================================================

/**
 * Enrichit les données d'une conversation avec les identifiants guest.
 * Pour les invités : user_id = null, identification via guest_fingerprint.
 * Pour les connectés : user_id inchangé (UUID Supabase).
 */
export async function enrichConversationForGuest(conv: {
  user_id?: string | null;
  guest_fingerprint?: string | null;
}): Promise<{
  user_id: string | null;
  guest_fingerprint: string;
  creator_type: "guest";
}> {
  const { fingerprint } = await getGuestIdentity();

  const isValidUuid =
    conv.user_id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      conv.user_id,
    );
  const userId = isValidUuid ? conv.user_id! : null;

  return {
    user_id: userId,
    guest_fingerprint: fingerprint,
    creator_type: "guest",
  };
}

// ============================================================================
// RÉCUPÉRATION DES POLLS GUEST
// ============================================================================

/**
 * Récupère les polls de l'invité depuis Supabase.
 * Identification uniquement par guest_fingerprint (creator_id est null pour les invités).
 */
export async function getGuestPolls(): Promise<any[]> {
  try {
    const { pollService } = await import("../services/PollService");
    const fingerprint = await getFingerprint();

    logger.info("🔍 GuestIdentity - Recherche polls par fingerprint", "auth", {
      fingerprint: fingerprint.substring(0, 16),
    });

    const polls = await pollService.getPollsByGuest(fingerprint);

    logger.info("🔍 GuestIdentity - Résultat", "auth", {
      count: polls.length,
    });

    return polls;
  } catch (error) {
    logger.warn(
      "⚠️ GuestIdentity - Erreur récupération polls Supabase",
      "auth",
      { error },
    );
    return [];
  }
}

// ============================================================================
// TEST HELPERS
// ============================================================================

/**
 * Réinitialise les caches (pour les tests uniquement).
 */
export function _resetCacheForTests(): void {
  deviceIdCache = null;
  fingerprintCache = null;
  guestOwnerTokenCache = null;
}
